import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, throwError, timer, of, Observable } from 'rxjs';
import { catchError, take, switchMap, map, first } from 'rxjs/operators';

import { TransactionService } from '../transaction.service';
import { OrderService } from '../order.service';
import { CheckoutRequest } from '../../../objects/checkout-request';
import { OrderTransactionActions, OrderActions } from '../actions';
import { BeachyError } from '../base';
import { OwnerActionService } from '../core-action-services';
import { Transaction } from 'src/objects/transaction';
import { createOrderPayload } from 'src/objects/order';



@Injectable()
export class OrderTransactionEffects {
  private actions$ = inject(Actions);
  private transactionService = inject(TransactionService);
  private orderService = inject(OrderService);
  private ownerActionService = inject(OwnerActionService);

  checkout$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTransactionActions.checkout),
      switchMap(({ token, customerDetails, order, marketingCommunications }) => {
        const request = new CheckoutRequest({ customer: customerDetails, marketingCommunications });
        request.payment.card.token = token;

        return this.transactionService
          .create(order, request.toBeachy())
          .pipe(
            map(transaction => OrderTransactionActions.transactionPolling({ order, transaction, timeoutInMs: 0 })),
            catchError(originalStackTrace => {
              let error = originalStackTrace;

              if (originalStackTrace.error?.messages) {
                try {
                  error = new BeachyError({ message: originalStackTrace.error.messages.join(), originalStackTrace });
                } catch {
                  error = originalStackTrace;
                }
              }
              return of(OrderTransactionActions.checkoutFailure({ error }));
            }),
          );
      }),
    ),
  );

  transactionPollingUpdateStatus$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTransactionActions.transactionPolling),
      map(({ transaction }) => OrderTransactionActions.checkoutStatusUpdate({ transaction })),
    ),
  );

  /**
   * This is a cyclic effect, it's used on purpose to
   * handle transaction polling
   */
  transactionPolling$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTransactionActions.transactionPolling),
      switchMap(({ order, transaction, timeoutInMs }) => combineLatest([
          this.transactionService
          .get(order, transaction)
          .pipe(
            catchError(error => throwError(error)),
          ),
          this.timeout(500).pipe(take(1)),
        ])
        .pipe(
          map(([trans]) => ({ transaction: trans as Transaction })),
          switchMap(res => {

            /**
             * Check if the timeout event should be fired
             */
            if ((timeoutInMs || 0) > 90000) {
              return throwError(new Error(
                'POLLING_TIMEOUT'));
            }

            if (res.transaction?.error) {
              /**
               * TODO: OLD FLOW: DID NOT ERROR HERE!
               */
              const message = res.transaction?.error?.message ?? 'An error ocurred';

              return of(OrderTransactionActions.checkoutFailure({
                error: new Error(message),
              }));
            }

            switch (res.transaction.state) {
              case 'failed': {
                return throwError(
                  new Error(res.transaction.detail?.error?.message ??'Transaction error'),
                );
              }
              case 'complete': {
                return this.orderService
                  .getByNumber({ orderNumber: order.token })
                  .pipe(
                    map(completedOrder => OrderTransactionActions.checkoutSuccess({
                      transaction: res.transaction,
                      completedOrder,
                    })),
                    catchError(error => throwError(error)),
                  );
              }
              /**
               * Lets do the polling and increase the timeSpent
               * value.
               */
              default: {
                const time = timeoutInMs + 500;

                return of(OrderTransactionActions.transactionPolling({ order, transaction, timeoutInMs: time }));
              }
            }
          }),
          catchError((error: Error) => of(OrderTransactionActions.checkoutFailure({ error }))),
        )),
    ),
  );

  /**
   * Creates a new order async when the current one completes
   */
  checkoutSuccessAskForANewOrderAsync$ = createEffect(() => this.actions$
    .pipe(
      ofType(OrderTransactionActions.checkoutSuccess),
      switchMap(({ completedOrder }) => this.ownerActionService.getStorefront().pipe(first(), map(storefront => ({completedOrder, storefront})))),
      map(({ completedOrder , storefront})  => createOrderPayload({storefront, order: completedOrder})),
      map((payload) => OrderActions.createOrder({payload})),
    ),
  );

  /**
   * Added just for testing purposes. It's way easier
   * to mock public method of a class and user marbles
   * than to deal with all fakeAsync/timer-tick stuff.
   */
  timeout = (timeInMs: number): Observable<number> => timer(timeInMs);
}

